// Phase 5.9.6 — best-effort device push cleanup at session boundaries.
//
// Web Push subscriptions are device-bound (not session-bound). Without an
// explicit unsubscribe on logout, the server keeps fanning out pushes to a
// device that has logged out — which is how a stray "Order delivered"
// notification can land on a phone that's logged out or has switched users.
//
// Used by AuthContext on logout and login (login also clears any prior-user
// orphan before the next subscribe).

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

/**
 * Remove any active push subscription this device currently holds — both on
 * the server (call /api/push/unsubscribe while the session cookie is still
 * valid) AND in the browser (sub.unsubscribe()).
 *
 * All errors are swallowed: this is best-effort plumbing that must never
 * block the auth flow.
 */
export async function clearDevicePushSubscription() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    // Tell server to forget this endpoint first — must happen BEFORE cookie
    // is cleared by /auth/logout. If it fails, we still try the browser side.
    try {
      const { default: axios } = await import('axios');
      await axios.post(
        `${API_URL}/api/push/unsubscribe`,
        { endpoint: sub.endpoint },
        { withCredentials: true, timeout: 4000 },
      );
    } catch {
      /* server side may already be gone; continue */
    }
    try {
      await sub.unsubscribe();
    } catch {
      /* browser side may already be gone */
    }
  } catch {
    /* nothing else to do — never block auth on this */
  }
}
